import {
  Card,
  CardContent,
  Grid,
  Typography,
  TableHead,
  TableRow,
  TableBody,
  Divider,
  Stack,
  Box,
  Backdrop,
  CircularProgress,
  Chip
} from '@mui/material'
import * as React from 'react'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableCell from '@mui/material/TableCell'

import { styled } from '@mui/material/styles'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import { useRouter } from 'next/router'
import { useMutation, useQuery } from 'react-query'
import {
  fetchAnalysisVariantDetails,
  fetchVariantDetails,
  getGeneCombination,
  GetGeneCombinationDto,
  getGenePairs,
  GetGenePairsDto,
  TranslateBulkDto,
  translates,
  VariantDetail,
  VariantDetailGeneInfo
} from '@/@core/services/app.service'
import GeneDetail from '@/views/analyze/VariationGene/GeneDetail'
import HealthConditionsDetail from '@/views/analyze/VariationGene/HealthConditionsDetail'
import { useTranslation } from 'react-i18next'
import Index from '@/views/analyze/Index'
import ACMGDetail from '@/views/analyze/VariationGene/ACMGDetail'
import { ExternalLinks } from '@/@core/services/api'
import { en } from '@faker-js/faker'
import { unique } from '@/@core/utils/utils'
import { NObject } from '../../filter-variants'

interface Props {
  params: any
  analysisInfo: any
}

// 通用单元格渲染器 —— 消除重复的 Box + Typography 模板代码
const renderCell = (value: any, row?: any, zhField?: string) => (
  <Box>
    <Typography sx={{ fontSize: '0.815rem' }}>
      {zhField && row?.[zhField] ? row[zhField] : value}
    </Typography>
  </Box>
)

const TableDetails = React.forwardRef((props: Props, ref) => {
  const router = useRouter()
  const { t, i18n } = useTranslation()

  const { id } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

  const [variantDetail, setVariantDetails] = React.useState<VariantDetail>()
  const [genePairs, setGenePairs] = React.useState<GetGenePairsDto[]>([])
  const [geneCombination, setGeneCombination] = React.useState<
    GetGeneCombinationDto[]
  >([])

  const { data } = useQuery<VariantDetail>(
    ['variantDetails'],
    async () => {
      const variants = {
        chrom: props.params.chrom,
        pos: props.params.pos,
        REF: props.params.REF || props.params.ref,
        ALT: props.params.ALT || props.params.alt,
        gene: props.params.gene,
        genomeBuild: props.analysisInfo.genomeBuild
      }

      if (type === 'sample')
        return await fetchVariantDetails({
          sampleId: id as unknown as number,
          variants
        })
      else {
        const analysisItemType = localStorage.getItem('analysisItemType')

        return await fetchAnalysisVariantDetails({
          analysisId: id as unknown as number,
          variants: {
            ...variants,
            analysisItemType
          }
        })
      }
    },
    {
      keepPreviousData: true,
      onSuccess: (variantDetails) => {
        setVariantDetails(variantDetails)
      }
    }
  )

  const genePhenotypeColunm = [
    {
      id: 'location' as 'code',
      label: 'Location',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'phenoName' as 'name',
      label: 'Phenotype',
      format: ((value: any, row: any) =>
        renderCell(
          i18n.language === 'zh' && row.pheno_name_cn
            ? row.pheno_name_cn
            : row.pheno_name
            ? row.pheno_name
            : value
        )) as any
    },
    {
      id: 'phenoMim' as 'name',
      label: 'Phenotype MIM number',
      format: ((_value: any, row: any) => (
        <Typography
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            fontSize: '0.815rem'
          }}
          onClick={() =>
            window.open(
              `${ExternalLinks.omim}/${row.phenoMim ?? row.pheno_mim_number}`
            )
          }
        >
          {row.phenoMim ?? row.pheno_mim_number}
        </Typography>
      )) as any
    }
  ]

  const oligoGeneColunm = [
    {
      id: 'genes' as 'code',
      label: 'genes',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'genesRelationship' as 'name',
      label: 'genes Relationship',
      format: ((value: any, row: any) =>
        renderCell(value, row, 'genesRelationship_zh')) as any
    },
    {
      id: 'proteinInteractions' as 'name',
      label: 'protein Interactions',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'commonPathways' as 'code',
      label: 'common Pathways',
      format: ((value: any, row: any) =>
        renderCell(value, row, 'commonPathways_zh')) as any
    },
    {
      id: 'geneMeta' as 'name',
      label: 'gene Meta',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'oligogenicVariantCombinations' as 'name',
      label: 'oligogenic Variant Combinations',
      format: ((value: any) => renderCell(value)) as any
    }
  ]

  const column2 = [
    {
      id: 'ENST' as 'code',
      label: 'Transcript ID',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'Transcript' as 'code',
      label: 'Transcript',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'length' as 'name',
      label: 'Length',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'refseq' as 'name',
      label: 'consequences',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'HGVSc' as 'name',
      label: 'HGVSc',
      format: ((value: any) => renderCell(value)) as any
    },
    {
      id: 'HGVSp' as 'name',
      label: 'HGVSp',
      format: ((value: any) => renderCell(value)) as any
    }
  ]

  const Translates = useMutation({
    mutationFn: async (data: { params: TranslateBulkDto }) => {
      try {
        return await translates(data.params)
      } catch (error) {
        return null
      }
    },
    onSuccess: (data: any[]) => {
      /* noop */
    }
  })

  const fetchGenePairs = useMutation({
    mutationFn: async (data: GetGenePairsDto) => await getGenePairs(data),
    onSuccess: (datas: GetGenePairsDto[]) => {
      if (datas && datas.length > 0) setGenePairs(datas)
    }
  })

  const fetchGeneCombination = useMutation({
    mutationFn: async (data: GetGeneCombinationDto) =>
      await getGeneCombination(data),
    onSuccess: async (datas: GetGeneCombinationDto[]) => {
      if (datas && datas.length > 0) {
        // 1. 收集所有需要翻译的唯一字符串
        const toTranslate = Array.from(
          new Set(
            datas.flatMap((d) =>
              [d.genesRelationship, d.commonPathways].filter(Boolean)
            )
          )
        )

        const sourceTextObj: NObject = {}
        toTranslate.forEach((item, index) => (sourceTextObj[index] = item))

        // 2. 调用批量翻译接口
        const translatesResult = await Translates.mutateAsync({
          params: {
            sourceText: sourceTextObj as any,
            sourceLanguage: 'en',
            targetLanguage: 'zh'
          }
        })

        // 3. 构建查表: 原文 -> 译文
        const translateMap = new Map<string, string>()
        translatesResult?.forEach((item: any) => {
          const original = Object.values(sourceTextObj).find(
            (_v, i) => String(i) === item.index
          )
          if (original && item.translated) {
            translateMap.set(original as string, item.translated)
          }
        })

        // 4. 将翻译结果合并回数据
        const merged = datas.map((i) => ({
          ...i,
          genesRelationship_zh: translateMap.get(i.genesRelationship ?? ''),
          commonPathways_zh: translateMap.get(i.commonPathways ?? '')
        }))

        setGeneCombination(merged)
      }
    }
  })

  React.useEffect(() => {
    if (props.params.gene) {
      fetchGenePairs.mutate({
        gene: props.params.gene
      })

      fetchGeneCombination.mutate({
        gene: props.params.gene
      })
    }
  }, [props.params.gene])

  return (
    <React.Fragment>
      <Card
        sx={{
          width: '99vw',
          position: 'sticky',
          left: 0,
          zIndex: 1
        }}
      >
        <CardContent>
          <Divider />

          <Grid container spacing={4}>
            <Grid item xs={6}>
              <Stack direction={'column'}>
                <Box sx={{ marginBottom: 4, width: '100%' }}>
                  <Typography
                    lineHeight={'2rem'}
                    pl={6}
                    sx={{
                      background: ' url(/images/pages/Framegene.png) no-repeat',
                      backgroundPosition: 'left, center',
                      bgcolor: '#EBF7FA',
                      color: '#08BCD4'
                    }}
                  >
                    {t('Gene Detail')}
                  </Typography>
                </Box>

                <GeneDetail
                  value={
                    {
                      geneInfo: variantDetail?.geneInfo,
                      geneMim: variantDetail?.geneMim,
                      genePli: variantDetail?.genePli,
                      variantInfo: variantDetail?.variantInfo ?? props.params,
                      transcripts: variantDetail?.transcripts,
                      hgncId: variantDetail?.hgncId
                    } as any
                  }
                />
              </Stack>

              <Stack direction={'column'}>
                <Box sx={{ marginBottom: 4 }}>
                  <Typography
                    lineHeight={'2rem'}
                    pl={6}
                    sx={{
                      background: ' url(/images/pages/Framegene.png) no-repeat',
                      backgroundPosition: 'left, center',
                      bgcolor: '#EBF7FA',
                      color: '#08BCD4'
                    }}
                  >
                    {t('Associated gene: Oligogene')}
                  </Typography>
                </Box>

                {geneCombination.length ? (
                  <Index columns={oligoGeneColunm} rows={geneCombination} />
                ) : (
                  ''
                )}
              </Stack>

              <Stack direction={'column'}>
                <Box sx={{ marginBottom: 4 }}>
                  <Typography
                    lineHeight={'2rem'}
                    pl={6}
                    sx={{
                      background: ' url(/images/pages/Framegene.png) no-repeat',
                      backgroundPosition: 'left, center',
                      bgcolor: '#EBF7FA',
                      color: '#08BCD4'
                    }}
                  >
                    {t('Associated genes: Double genes')}
                  </Typography>
                </Box>

                <Stack spacing={2} direction="row">
                  {unique(genePairs, 'pair').map((i) => (
                    <Chip key={i.pair} label={i.pair} color="primary" />
                  ))}
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={6}>
              <Stack spacing={4}>
                <Stack direction={'column'}>
                  <Box sx={{ marginBottom: 4 }}>
                    <Typography
                      lineHeight={'2rem'}
                      pl={6}
                      sx={{
                        background:
                          ' url(/images/pages/Framegene.png) no-repeat',
                        backgroundPosition: 'left, center',
                        bgcolor: '#EBF7FA',
                        color: '#08BCD4'
                      }}
                    >
                      {t('Gene-Phenotype Relationships')}
                    </Typography>
                  </Box>

                  <Index
                    columns={genePhenotypeColunm}
                    rows={
                      props.params.OMIM
                        ? props.params.OMIM.omimData
                        : props.params.omim_phenotypes
                        ? props.params.omim_phenotypes
                        : []
                    }
                  />

                  <Box mt={4}>
                    <Index
                      columns={column2}
                      rows={
                        variantDetail?.transcripts.length
                          ? variantDetail?.transcripts
                          : props.params.features
                          ? props.params.features.map((i: any) => ({
                              ENST: i.feature,
                              length: i.cdna_length,
                              refseq: i.consequence,
                              HGVSc: i.hgvsc,
                              HGVSp: i.hgvsp,
                              Transcript: props.params.feature
                            }))
                          : []
                      }
                      key={variantDetail?.transcripts.length}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </React.Fragment>
  )
})

export default TableDetails
